import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@app/core/helperclasses/basecomponent';
import { TrackingService } from '@app/core/tracking.service';
import { QuestionHintDialogComponent } from '@app/dialogs/question-hint-dialog/question-hint-dialog.component';
import {
  Question,
  QuestionAnswer,
  SolveQuestionResult,
} from '@app/models/question';
import { environment } from '@env/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ExerciseTaskService } from './exercise-task-module.service';
import { ID } from '@app/models';

export interface CollectedAnswer {
  question_id: ID;
  answer_text?: string;
  answers?: { id: ID }[];
}

@UntilDestroy()
@Component({
  selector: 'exercise-task-module',
  templateUrl: './exercise-task-module.component.html',
  styleUrls: ['./exercise-task-module.style.scss'],
  host: {
    class: 'exercise-task-pm',
  },
})
export class ExerciseTaskModuleComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() config!: Question;
  @Input() componentBaseUrl: string = '';
  @Input() resultMode: boolean = false;
  @Input() onlyOneQuestion: boolean = false;
  @Input() showQuestionNumber: boolean = true;
  // When true the question is not solved server-side and no correct/incorrect feedback is shown;
  // instead the chosen answer is emitted via answerCollected so a parent can batch-submit later.
  @Input() deferSubmit: boolean = false;
  // Zuletzt für QUESTION_START getrackte Frage-ID (verhindert Doppel-Emits bei
  // wiederholten ngOnChanges für dieselbe Frage).
  private lastTrackedQuestionId = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() nextQuestionEmitter: EventEmitter<any> = new EventEmitter();
  @Output() resultModeEmitter: EventEmitter<boolean> = new EventEmitter();
  @Output() questionAnswerSet: EventEmitter<boolean> = new EventEmitter();
  @Output() questionAnswered: EventEmitter<SolveQuestionResult> =
    new EventEmitter();
  @Output() answerCollected: EventEmitter<CollectedAnswer> = new EventEmitter();

  public baseUrl =
    environment.serverUrl + environment.apiVersion + '/images/images/';

  public isBookmarked: boolean = false;

  public solvedCorrect: boolean = false;
  public solvedInCorrect: boolean = false;

  public answersModel: boolean[] = [];
  public freeTextAnswer: string = '';

  public answers!: QuestionAnswer[] | undefined;

  public secondAnswerMode: boolean = false;

  public resultData: SolveQuestionResult | undefined = undefined;

  public resultImgLoaded: boolean = false;

  public disableSolveButton: boolean = true;

  public answersWithQuestions: boolean = false;

  constructor(
    private service: ExerciseTaskService,
    private dialog: MatDialog,
    private trackingService: TrackingService,
  ) {
    super();
  }

  toggleBookmark() {
    this.isBookmarked = !this.isBookmarked;
    this.bookmarkQuestions();
  }

  ngOnInit() {
    if (this.config) {
      this.isBookmarked =
        this.config && this.config.user_data && this.config.user_data.is_marked;
      this.service.componentBaseUrl = this.componentBaseUrl;
      this.setInitAnswers(this.config.answers);
      this.initializeAnswersModel();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.answers = undefined;
      this.answersWithQuestions = false;
      this.answersModel = [];
      this.freeTextAnswer = '';
      this.solvedCorrect = false;
      this.solvedInCorrect = false;
      this.resultImgLoaded = false;
      this.resultMode = false;
      this.resultData = undefined;
      this.isBookmarked =
        this.config && this.config.user_data && this.config.user_data.is_marked;
      this.resultModeEmitter.emit(false);
      this.setInitAnswers(this.config.answers);
      this.initializeAnswersModel();
      // Tracking: QUESTION_START pro Frage. ngOnChanges deckt ALLE Fälle ab –
      // Einzel-/Set-Dialog (config wird gesetzt/durchgetauscht) und Weiter/Zurück
      // in Übung/Prüfung (dieselbe Instanz, nur neues config). Dedup pro Frage-ID.
      if (this.config && this.config.id) {
        const questionId = String(this.config.id);
        if (questionId !== this.lastTrackedQuestionId) {
          this.lastTrackedQuestionId = questionId;
          this.trackingService.trackQuestionStart(
            questionId,
            this.componentBaseUrl,
          );
        }
      }
    }
  }

  setInitAnswers(answers: QuestionAnswer[]) {
    if (
      this.config &&
      (this.config.questiontype == 'SC' || this.config.questiontype == 'MC')
    ) {
      this.answers = answers; //JSON.parse(JSON.stringify(answers));
      this.answersWithQuestions =
        this.answers.findIndex((answer) => answer.image_uid) !== -1;
    }
  }

  initializeAnswersModel() {
    this.secondAnswerMode = false;
    this.resultImgLoaded = false;
    this.resultMode = false;
    this.resultData = undefined;
    this.answersModel = [];
    this.freeTextAnswer = '';
    for (let i = 0; i < (!this.answers ? 0 : this.answers.length); i++) {
      this.answersModel.push(false);
    }
  }

  solveQuestion() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const answerModel: any = {};
    if (
      this.config.questiontype == 'SC' ||
      this.config.questiontype == 'MC' ||
      (this.config.questiontype == 'FREE_SC' && this.secondAnswerMode)
    ) {
      answerModel.answers = [];
      for (let index = 0; index < this.answersModel.length; index++) {
        const answer = this.answersModel[index];
        if (answer && this.answers) {
          answerModel.answers.push({ id: this.answers[index].id });
        }
      }
    } else if (
      (this.config.questiontype == 'FREE' ||
        this.config.questiontype == 'FREE_SC') &&
      !this.secondAnswerMode
    ) {
      answerModel.answer_text = this.freeTextAnswer;
    } else {
      return;
    }
    if (!answerModel) {
      return;
    }

    // Collect-only mode: emit the chosen answer without solving, so the parent can submit the whole
    // set at once. No correctness is revealed here.
    if (this.deferSubmit) {
      this.answerCollected.emit({
        question_id: this.config.id,
        answer_text: answerModel.answer_text,
        answers: answerModel.answers,
      });
      return;
    }

    this.addSubscription(
      this.service.solveQuestion(this.config.id, answerModel),
    ).subscribe((resp) => {
      this.solvedInCorrect = !resp?.data.is_correct;
      this.solvedCorrect = resp?.data.is_correct;
      if (
        this.config.questiontype == 'FREE_SC' &&
        !resp?.data.is_correct &&
        !this.answers
      ) {
        this.secondAnswerMode = true;
      } else {
        this.resultMode = true;
        this.resultData = resp?.data;
        this.questionAnswered.emit(resp?.data);
        this.resultModeEmitter.emit(true);
      }
      if (
        this.config.questiontype == 'SC' ||
        this.config.questiontype == 'MC' ||
        (this.config.questiontype == 'FREE_SC' &&
          this.secondAnswerMode &&
          this.answers)
      ) {
        this.answers = this.setAnswerSolutions(
          resp?.data.answers ? resp?.data.answers : this.config.answers,
        );
      } else if (this.config.questiontype == 'FREE_SC') {
        this.answers = resp?.data.answers ? resp?.data.answers : [];
      }
    });
  }

  nextQuestion() {
    this.nextQuestionEmitter.emit();
  }

  setAnswerSolutions(newAnswers: QuestionAnswer[]): QuestionAnswer[] {
    if (this.answers) {
      for (let i = 0; i < (!this.answers ? 0 : this.answers.length); i++) {
        const answer = this.answers[i];

        const newAnswer = newAnswers.find(
          (haystack) => haystack.id == answer.id,
        );
        if (!newAnswer) {
          continue;
        }
        this.answers[i] = { ...answer, ...newAnswer };
      }

      return JSON.parse(JSON.stringify(this.answers));
    }
    return [];
  }

  bookmarkQuestions() {
    this.addSubscription(
      this.service.bookmarkQuestions(this.config.id),
    ).subscribe(() => {});
  }

  preventChange(event: Event) {
    if (this.resultMode) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  openQuestionHintDialog() {
    this.addSubscription(
      this.dialog
        .open(QuestionHintDialogComponent, {
          data: {
            hintData: {
              information_image: this.config.image_hint_uid,
              information_text: this.config.question_hint,
            },
          },
        })
        .afterClosed(),
    ).subscribe(() => {});
  }

  onAnswerChange(index: number) {
    if (
      this.config.questiontype == 'SC' ||
      this.config.questiontype == 'FREE_SC'
    ) {
      // this.answersModel = this.answersModel.map((value, i) => i === index);
      for (let i = 0; i < this.answersModel.length; i++) {
        if (i != index) {
          this.answersModel[i] = false;
        }
      }
    }
    this.answers?.forEach((answer, index) => {
      answer.is_checked = this.answersModel[index];
    });

    if (this.answers?.findIndex((answer) => answer.is_checked) != -1) {
      this.disableSolveButton = false;
      this.questionAnswerSet.emit(true);
    } else {
      this.disableSolveButton = true;
      this.questionAnswerSet.emit(false);
    }
  }

  handleAnswerInputChange() {
    if (
      this.config.questiontype == 'FREE' ||
      this.config.questiontype == 'FREE_SC'
    ) {
      if (this.freeTextAnswer) {
        this.disableSolveButton = false;
        this.questionAnswerSet.emit(true);
      } else {
        this.disableSolveButton = true;
        this.questionAnswerSet.emit(false);
      }
    }
  }
}
